// Claude Ad Writer Service
import { AdGenerationRequest, GeneratedAd } from '../types/googleAds.types';

export class ClaudeAdWriter {
  private apiKey: string;
  private apiUrl = 'https://api.anthropic.com/v1/messages';

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async generateAds(request: AdGenerationRequest): Promise<GeneratedAd> {
    const prompt = this.buildPrompt(request);
    
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          max_tokens: 1000,
          messages: [{
            role: 'user',
            content: prompt
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate ads');
      }

      const data = await response.json();
      return this.parseResponse(data.content[0].text);
    } catch (error) {
      console.error('Error generating ads:', error);
      throw error;
    }
  }

  private buildPrompt(request: AdGenerationRequest): string {
    return `Jesteś ekspertem Google Ads specjalizującym się w tworzeniu skutecznych reklam dla firm produkcyjnych.

Stwórz teksty reklamowe Google Ads dla:
- Produkt: ${request.product_name}
- Opis: ${request.product_description}
- Słowa kluczowe: ${request.keywords.join(', ')}
${request.target_audience ? `- Grupa docelowa: ${request.target_audience}` : ''}
${request.unique_selling_points ? `- USP: ${request.unique_selling_points.join(', ')}` : ''}
${request.call_to_action ? `- Wezwanie do działania: ${request.call_to_action}` : ''}
${request.include_price && request.price ? `- Cena: ${request.price}` : ''}

Wymagania:
- Nagłówki: max 30 znaków każdy (wygeneruj 10 wariantów)
- Opisy: max 90 znaków każdy (wygeneruj 5 wariantów)
- Ton: ${request.tone === 'professional' ? 'profesjonalny' : 
         request.tone === 'friendly' ? 'przyjazny' : 
         request.tone === 'urgent' ? 'pilny/wzbudzający poczucie pilności' : 'informacyjny'}
- Użyj silnych słów sprzedażowych
- Podkreśl korzyści dla klienta
- Zawrzyj wezwanie do działania

Dodatkowo zasugeruj:
- 10 dodatkowych słów kluczowych
- Szacunkowy CTR (w %)
- Szacunkowy Quality Score (1-10)

Zwróć odpowiedź w formacie JSON:
{
  "headlines": ["nagłówek1", "nagłówek2", ...],
  "descriptions": ["opis1", "opis2", ...],
  "suggested_keywords": ["słowo1", "słowo2", ...],
  "estimated_performance": {
    "ctr": liczba,
    "quality_score": liczba
  }
}`;
  }

  private parseResponse(response: string): GeneratedAd {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error parsing AI response:', error);
    }

    // Fallback
    return {
      headlines: [
        'Profesjonalne Plexi',
        'Zamów Plexi Online',
        'Plexi Na Wymiar'
      ],
      descriptions: [
        'Najwyższa jakość pleksi. Szybka realizacja. Sprawdź ofertę!',
        'Producent elementów z plexi. Indywidualne projekty. Zadzwoń!'
      ],
      suggested_keywords: ['plexi na wymiar', 'producent plexi', 'pleksi gdańsk'],
      estimated_performance: {
        ctr: 3.5,
        quality_score: 7
      }
    };
  }

  async analyzeCompetitorAds(competitorDomain: string, keywords: string[]): Promise<{
    insights: string[];
    recommendations: string[];
  }> {
    const prompt = `Przeanalizuj możliwe strategie reklamowe konkurenta ${competitorDomain} 
    dla słów kluczowych: ${keywords.join(', ')}.
    
    Zwróć insights i rekomendacje w formacie JSON:
    {
      "insights": ["spostrzeżenie1", "spostrzeżenie2"],
      "recommendations": ["rekomendacja1", "rekomendacja2"]
    }`;

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          max_tokens: 500,
          messages: [{
            role: 'user',
            content: prompt
          }]
        })
      });

      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error analyzing competitor ads:', error);
    }

    return {
      insights: [
        'Konkurent prawdopodobnie targetuje lokalne słowa kluczowe',
        'Może używać rozszerzeń cenowych w reklamach'
      ],
      recommendations: [
        'Dodaj lokalne modyfikatory do słów kluczowych',
        'Testuj różne wezwania do działania'
      ]
    };
  }

  async optimizeAdCopy(currentAd: {
    headlines: string[];
    descriptions: string[];
    performance: { ctr: number; conversions: number };
  }): Promise<{
    optimized_headlines: string[];
    optimized_descriptions: string[];
    optimization_rationale: string[];
  }> {
    const prompt = `Zoptymalizuj poniższe teksty reklamowe Google Ads:

Obecne nagłówki: ${currentAd.headlines.join(', ')}
Obecne opisy: ${currentAd.descriptions.join(', ')}
Wydajność: CTR ${currentAd.performance.ctr}%, Konwersje: ${currentAd.performance.conversions}

Zaproponuj ulepszone wersje i wyjaśnij dlaczego będą lepsze.

Zwróć w formacie JSON:
{
  "optimized_headlines": ["nowy1", "nowy2", "nowy3"],
  "optimized_descriptions": ["nowy_opis1", "nowy_opis2"],
  "optimization_rationale": ["powód1", "powód2"]
}`;

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-opus-20240229',
          max_tokens: 700,
          messages: [{
            role: 'user',
            content: prompt
          }]
        })
      });

      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error optimizing ad copy:', error);
    }

    return {
      optimized_headlines: currentAd.headlines,
      optimized_descriptions: currentAd.descriptions,
      optimization_rationale: ['Brak sugestii optymalizacji']
    };
  }
}

// Szablony reklam dla różnych branż/produktów
export const adTemplates = {
  productSales: {
    name: 'Sprzedaż produktu',
    headlines: [
      '{ProductName} - Kup Teraz',
      'Najlepsze {ProductName}',
      '{ProductName} w Super Cenie',
      'Promocja na {ProductName}',
      '{ProductName} - Wysoka Jakość'
    ],
    descriptions: [
      'Szeroki wybór {ProductCategory}. Szybka wysyłka. Sprawdź teraz!',
      'Najwyższa jakość w najlepszej cenie. Zamów {ProductName} już dziś!',
      'Profesjonalne {ProductCategory}. Gwarancja satysfakcji. Zobacz ofertę!'
    ]
  },
  
  b2bServices: {
    name: 'Usługi B2B',
    headlines: [
      '{ServiceName} dla Firm',
      'Profesjonalne {ServiceName}',
      '{ServiceName} - Eksperci',
      'Outsourcing {ServiceCategory}',
      '{ServiceName} B2B'
    ],
    descriptions: [
      'Kompleksowe {ServiceCategory} dla biznesu. Doświadczony zespół. Sprawdź!',
      'Zwiększ efektywność z naszymi {ServiceCategory}. Bezpłatna wycena!',
      'Zaufany partner w {ServiceCategory}. Indywidualne podejście. Kontakt!'
    ]
  },
  
  localBusiness: {
    name: 'Biznes lokalny',
    headlines: [
      '{BusinessName} {City}',
      '{Service} w {City}',
      'Najlepszy {Service} {City}',
      '{City} - {BusinessName}',
      '{Service} Blisko Ciebie'
    ],
    descriptions: [
      '{Service} w {City} i okolicach. Konkurencyjne ceny. Umów się!',
      'Lokalny {BusinessType} z tradycjami. Profesjonalna obsługa. Zapraszamy!',
      '{Years} lat doświadczenia w {City}. Sprawdzona jakość. Zadzwoń!'
    ]
  }
};
